import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  ListAltOutlined,
  SearchRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { RegistrationStatus, SalesStatus } from "../../../Models/ambassador";
import { useMyDeals, useMyDealSummary } from "../../../Query/useAmbassador";
import { useDebouncedCallback } from "../../../Hooks/useDebouncedCallback";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  StatGrid,
  StatTile,
} from "../Common/AmbassadorUi";
import { ambassadorPaths } from "../Common/ambassadorNavigation";
import {
  EXTERNAL_SALES_STATUSES,
  REGISTRATION_STATUSES,
  useAmbassadorLabels,
} from "../Common/useAmbassadorLabels";
import MyDealsTable from "./MyDealsTable";

const PAGE_SIZE = 10;

const parseStatus = (value: string | null): RegistrationStatus | null => {
  const status = Number(value);
  return value !== null && REGISTRATION_STATUSES.includes(status)
    ? status
    : null;
};

const MyDeals: React.FC = () => {
  const { t } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const navigate = useNavigate();
  // ?status=<n> opens the list filtered, e.g. from the open questions notice.
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [registrationStatus, setRegistrationStatus] =
    useState<RegistrationStatus | null>(() =>
      parseStatus(searchParams.get("status")),
    );
  const [salesStatus, setSalesStatus] = useState<SalesStatus | null>(null);

  const { data: summary } = useMyDealSummary();
  const deals = useMyDeals({
    page,
    page_size: PAGE_SIZE,
    search: search || undefined,
    registration_status: registrationStatus,
    sales_status: salesStatus,
  });

  const updateSearch = useDebouncedCallback((value: string) => {
    setSearch(value.trim());
    setPage(1);
  }, 300);

  const filtered = Boolean(
    search || registrationStatus !== null || salesStatus !== null,
  );
  const pageCount = Math.ceil((deals.data?.total ?? 0) / PAGE_SIZE);

  const newDealButton = (
    <Button
      variant="contained"
      startIcon={<AddRounded />}
      onClick={() => navigate(ambassadorPaths.newDeal)}
    >
      {t("navigation.newDeal")}
    </Button>
  );

  return (
    <>
      <PageHeader
        title={t("myDeals.title")}
        subtitle={t("myDeals.subtitle")}
        actions={newDealButton}
      />

      <StatGrid>
        <StatTile label={t("myDeals.stats.total")} value={summary?.total} />
        <StatTile
          label={t("myDeals.stats.inReview")}
          value={summary?.in_review}
        />
        <StatTile
          label={t("myDeals.stats.questions")}
          value={summary?.questions_pending}
          highlight={!!summary?.questions_pending}
        />
        <StatTile
          label={t("myDeals.stats.confirmed")}
          value={summary?.confirmed}
        />
      </StatGrid>

      <Panel title={t("myDeals.listTitle")} subtitle={t("myDeals.sortHint")}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) 1fr 1fr" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <TextField
            size="small"
            value={searchInput}
            placeholder={t("myDeals.searchPlaceholder")}
            onChange={(event) => {
              setSearchInput(event.target.value);
              updateSearch(event.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: color.gray400 }} />
                </InputAdornment>
              ),
            }}
            inputProps={{ "aria-label": t("myDeals.searchPlaceholder") }}
          />
          <TextField
            select
            size="small"
            label={t("myDeals.registrationFilter")}
            value={registrationStatus ?? ""}
            onChange={(event) => {
              setRegistrationStatus(
                event.target.value === "" ? null : Number(event.target.value),
              );
              setPage(1);
            }}
          >
            <MenuItem value="">{t("common.all")}</MenuItem>
            {REGISTRATION_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {labels.registrationStatus(status)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t("myDeals.salesFilter")}
            value={salesStatus ?? ""}
            onChange={(event) => {
              setSalesStatus(
                event.target.value === "" ? null : Number(event.target.value),
              );
              setPage(1);
            }}
          >
            <MenuItem value="">{t("common.all")}</MenuItem>
            {EXTERNAL_SALES_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {labels.salesStatus(status)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {deals.isLoading ? (
          <LoadingState />
        ) : deals.isError ? (
          <ErrorState onRetry={() => deals.refetch()} />
        ) : deals.data?.deals.length ? (
          <>
            <MyDealsTable deals={deals.data.deals} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mt: 2,
              }}
            >
              <Typography sx={{ fontSize: 13, color: color.gray500 }}>
                {t("common.shownOf", {
                  shown: deals.data.deals.length,
                  total: deals.data.total,
                })}
              </Typography>
              {pageCount > 1 && (
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size="small"
                />
              )}
            </Box>
          </>
        ) : filtered ? (
          <EmptyState
            title={t("myDeals.noResultsTitle")}
            text={t("myDeals.noResultsText")}
          />
        ) : (
          <EmptyState
            icon={<ListAltOutlined />}
            title={t("myDeals.emptyTitle")}
            text={t("myDeals.emptyText")}
            action={newDealButton}
          />
        )}
      </Panel>
    </>
  );
};

export default MyDeals;
